// stores/panelDataMutations.ts — pure candle/state helpers for panelDataStore.
//
// Extracted to keep the store impl <250 LOC. Every function here is pure: it
// takes state (or arrays) and returns new arrays/objects, never mutating input.
// The candle-gate state machine (§4.4) and per-symbol WS gate (§4.3) live in
// the store; these are the building blocks it composes.

import type { CandleData } from './priceDataStore';
import type { EmaPoint, RsiPoint, TradeFlowPoint } from './indicatorStore';
import type {
  IntervalKey,
  PanelDataEntry,
  PanelDataState,
  PanelSignalEntry,
  SymbolKey,
} from './panelDataStore.types';

/** Bound the WS pre-seed buffer (≈4h of 1m bars) so a never-promoting symbol
 *  can't grow unboundedly; oldest dropped on overflow (NEW-4). */
export const BUFFER_MAX = 240;

/** Sort by startTs ascending and drop duplicate timestamps, keeping the LAST
 *  occurrence (newer write wins — matters when a live bar updates a buffered one). */
export function dedupeSorted(candles: readonly CandleData[]): CandleData[] {
  if (candles.length === 0) return [];
  const sorted = [...candles].sort((a, b) => a.startTs - b.startTs);
  const out: CandleData[] = [];
  for (const c of sorted) {
    const last = out[out.length - 1];
    if (last !== undefined && last.startTs === c.startTs) {
      out[out.length - 1] = c; // last write wins
    } else {
      out.push(c);
    }
  }
  return out;
}

/** Merge one live candle into a ready entry: update the matching-ts bar in place,
 *  or append if it's newer. Assumes `entry.candles` is sorted ascending. */
export function mergeCandle(entry: PanelDataEntry, candle: CandleData): PanelDataEntry {
  const candles = entry.candles;
  const lastTs = candles.length > 0 ? candles[candles.length - 1]!.startTs : -Infinity;
  let next: CandleData[];
  if (candle.startTs > lastTs) {
    next = [...candles, candle]; // strictly newer bar → append
  } else if (candle.startTs === lastTs) {
    next = candles.slice(0, -1).concat(candle); // same bucket → replace tail
  } else {
    // Out-of-order/older bar (rare): splice in via dedupeSorted to stay sorted.
    next = dedupeSorted([...candles, candle]);
  }
  return { ...entry, candles: next, lastWsCandleAt: candle.startTs };
}

/** Build a fresh entry for a symbol (used when no prior entry exists). */
export function makeEntry(
  symbol: SymbolKey,
  candles: CandleData[],
  tradeFlow: TradeFlowPoint[],
  loadState: PanelDataEntry['loadState'],
): PanelDataEntry {
  return {
    symbol,
    candles: dedupeSorted(candles),
    tradeFlow,
    loadState,
    lastWsCandleAt: 0,
  };
}

// --- state-transition helpers (return partial state for set(...)) ---

/** idle → loading (blocks the snapshot seed while REST is in flight, NEW-3). */
export function markLoading(s: PanelDataState, symbol: SymbolKey): Partial<PanelDataState> {
  const prior = s.dataByKey[symbol];
  const entry: PanelDataEntry = prior
    ? { ...prior, loadState: 'loading' }
    : makeEntry(symbol, [], [], 'loading');
  return { dataByKey: { ...s.dataByKey, [symbol]: entry } };
}

/** loading → awaiting-seed (REST returned 0 candles — sparse pair, NEW-4). The
 *  trade flow that did come back is retained so the panel isn't fully blank. */
export function markAwaitingSeed(
  s: PanelDataState,
  symbol: SymbolKey,
  flow: TradeFlowPoint[],
): Partial<PanelDataState> {
  const prior = s.dataByKey[symbol];
  const entry: PanelDataEntry = prior
    ? { ...prior, tradeFlow: flow, loadState: 'awaiting-seed' }
    : makeEntry(symbol, [], flow, 'awaiting-seed');
  return { dataByKey: { ...s.dataByKey, [symbol]: entry } };
}

/** loading → error (REST threw). Keeps any prior candles so the chart isn't wiped. */
export function markError(s: PanelDataState, symbol: SymbolKey): Partial<PanelDataState> {
  const prior = s.dataByKey[symbol];
  const entry: PanelDataEntry = prior
    ? { ...prior, loadState: 'error' }
    : makeEntry(symbol, [], [], 'error');
  return { dataByKey: { ...s.dataByKey, [symbol]: entry } };
}

/** Remove a key from a Record without mutating it. */
export function omitKey<V>(record: Record<string, V>, key: string): Record<string, V> {
  const { [key]: _drop, ...rest } = record;
  return rest;
}

// --- signal slice (symbol:interval) helpers ---

/** A fresh, empty signal entry for a symbol:interval key. */
export function blankSignal(key: IntervalKey): PanelSignalEntry {
  const [symbol, intervalStr] = key.split(':');
  return {
    symbol: symbol ?? key,
    interval: Number(intervalStr ?? '0'),
    ema: {} as Record<number, EmaPoint[]>,
    rsi: [] as RsiPoint[],
    macd: [],
    sma: {},
  };
}

/** Drop every signal key belonging to a symbol (used in clear, §4.2). */
export function clearSignalsForSymbol(
  signalByKey: Record<IntervalKey, PanelSignalEntry>,
  symbol: SymbolKey,
): Record<IntervalKey, PanelSignalEntry> {
  const prefix = `${symbol}:`;
  const out: Record<string, PanelSignalEntry> = {};
  for (const [k, v] of Object.entries(signalByKey)) {
    if (!k.startsWith(prefix)) out[k] = v;
  }
  return out as Record<IntervalKey, PanelSignalEntry>;
}

/** Drain buffered live bars in timestamp order via appendCandle (now 'ready'). */
export function drainBuffer(
  get: () => PanelDataState,
  symbol: SymbolKey,
  buffered: CandleData[] | undefined,
): void {
  if (!buffered || buffered.length === 0) return;
  const ordered = dedupeSorted(buffered);
  const append = get().appendCandle;
  for (const c of ordered) append(symbol, c);
}
